import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../utils/cn"

export type ToastPosition = "top-center" | "top-right" | "bottom-right"

export function getSwipeDirection(position: ToastPosition): "up" | "down" | "left" | "right" {
    switch (position) {
        case "top-center":
            return "up"
        case "top-right":
        case "bottom-right":
            return "right"
        default:
            return "right"
    }
}

type ToastProviderProps = React.ComponentPropsWithoutRef<typeof ToastPrimitives.Provider> & {
    position?: ToastPosition
}

const ToastProvider = ({ position, swipeDirection, ...props }: ToastProviderProps) => {
    return (
        <ToastPrimitives.Provider
            swipeDirection={swipeDirection ?? (position ? getSwipeDirection(position) : "right")}
            {...props}
        />
    )
}

const toastViewportVariants = cva(
    "fixed z-[100] flex max-h-screen w-full p-3 sm:p-4",
    {
        variants: {
            position: {
                "top-center": "top-0 left-1/2 -translate-x-1/2 flex-col-reverse sm:max-w-[420px]",
                "top-right": "top-0 right-0 flex-col-reverse sm:max-w-[420px]",
                "bottom-right": "bottom-0 right-0 flex-col sm:max-w-[420px]",
            },
        },
        defaultVariants: {
            position: "bottom-right",
        },
    }
)

const ToastViewport = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Viewport>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> & {
        position?: ToastPosition
    }
>(({ className, position = "bottom-right", ...props }, ref) => (
    <ToastPrimitives.Viewport
        ref={ref}
        className={cn(toastViewportVariants({ position }), className)}
        {...props}
    />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
    "group pointer-events-auto relative flex w-full items-center justify-between space-x-3 sm:space-x-4 overflow-hidden rounded-sm border-brutal border-ink bg-paper p-3 pr-5 sm:p-6 sm:pr-8 shadow-brutal-md transition-all duration-200 ease-out select-none touch-pan-y data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80",
    {
        variants: {
            variant: {
                default: "border-ink bg-paper",
                info: "border-sky bg-sky/90",
                success: "border-status-completed bg-status-completed/90",
                warning: "border-status-generating bg-status-generating/90",
                error: "border-status-failed bg-status-failed/90",
            },
            position: {
                "top-center": "touch-pan-x data-[state=closed]:slide-out-to-top-full data-[state=open]:slide-in-from-top-full",
                "top-right": "touch-pan-y data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full",
                "bottom-right": "touch-pan-y data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full",
            },
        },
        defaultVariants: {
            variant: "default",
            position: "bottom-right",
        },
    }
)

const Toast = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Root>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, position, ...props }, ref) => {
    return (
        <ToastPrimitives.Root
            ref={ref}
            className={cn(toastVariants({ variant, position }), className)}
            {...props}
        />
    )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Action>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Action
        ref={ref}
        className={cn(
            "inline-flex h-8 shrink-0 items-center justify-center rounded-sm border-brutal border-ink bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-status-failed group-[.destructive]:hover:border-status-failed group-[.destructive]:hover:bg-status-failed/20",
            className
        )}
        {...props}
    />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastTitle = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Title>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Title
        ref={ref}
        className={cn("text-sm font-semibold", className)}
        {...props}
    />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Description>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
    <ToastPrimitives.Description
        ref={ref}
        className={cn("text-sm opacity-90", className)}
        {...props}
    />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
    type ToastProps,
    type ToastActionElement,
    ToastProvider,
    ToastViewport,
    Toast,
    ToastTitle,
    ToastDescription,
    ToastAction,
}
